import { z } from 'zod';
import { RoleSchema, WorkspaceKindSchema, WorkspaceSchema } from './identity';

/**
 * Access administration contracts (M2-02, ADR-008). Requests are untrusted input: the Worker
 * validates them with these schemas and takes organisation and scope only from stored records.
 */

const Name = z.string().trim().min(2).max(120);

export const OrganizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.literal('active'),
  createdAt: z.string(),
});
export type Organization = z.infer<typeof OrganizationSchema>;

export const AdminWorkspaceSchema = WorkspaceSchema.extend({ createdAt: z.string() });
export type AdminWorkspace = z.infer<typeof AdminWorkspaceSchema>;

export const DirectorySchema = z.object({
  organizations: z.array(OrganizationSchema),
  workspaces: z.array(AdminWorkspaceSchema),
});
export type Directory = z.infer<typeof DirectorySchema>;

export const MembershipStatusSchema = z.enum(['active', 'revoked']);

export const WorkspaceMemberSchema = z.object({
  uid: z.string(),
  email: z.string().nullable(),
  displayName: z.string().nullable(),
  roles: z.array(RoleSchema),
  status: MembershipStatusSchema,
  grantedAt: z.string(),
});
export type WorkspaceMember = z.infer<typeof WorkspaceMemberSchema>;

export const AuditActionSchema = z.enum([
  'organization.create',
  'workspace.create',
  'membership.grant',
  'membership.revoke',
]);

export const AuditEventSchema = z.object({
  id: z.string(),
  action: AuditActionSchema,
  actorUid: z.string(),
  targetId: z.string(),
  workspaceId: z.string().nullable(),
  detail: z.string(),
  at: z.string(),
});
export type AuditEvent = z.infer<typeof AuditEventSchema>;

export const CreateOrganizationRequestSchema = z.object({ name: Name }).strict();
export type CreateOrganizationRequest = z.infer<typeof CreateOrganizationRequestSchema>;

export const CreateWorkspaceRequestSchema = z
  .object({ kind: WorkspaceKindSchema, name: Name, organizationId: z.string().min(1).optional() })
  .strict();
export type CreateWorkspaceRequest = z.infer<typeof CreateWorkspaceRequestSchema>;

export const GrantMembershipRequestSchema = z
  .object({
    email: z.string().trim().toLowerCase().pipe(z.email()),
    roles: z.array(RoleSchema).min(1).max(4),
  })
  .strict();
export type GrantMembershipRequest = z.infer<typeof GrantMembershipRequestSchema>;

/** `GET /api/v1/workspaces/:id` — the caller's own scope in one workspace. */
export const WorkspaceAccessSchema = z.object({
  workspace: WorkspaceSchema,
  roles: z.array(RoleSchema),
  permissions: z.array(z.string()),
});
export type WorkspaceAccess = z.infer<typeof WorkspaceAccessSchema>;
